import React, { JSXElementConstructor, PropsWithChildren } from 'react';
import classes from './NodeBox.module.scss';
import classNames from 'classnames';
import Typography from 'new/elements/Typography';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import { Stack, Tooltip } from '@mui/material';
import { Footer } from 'js/components/pipelines/cards/components/NodeBox/Footer';
import { useZoomState } from 'js/components/pipelines/FlowNodes/hooks/useZoomState';

interface NodeBoxProps {
  className?: string;
  onClick?: () => void;
  size?: 'small' | 'large';
  empty?: boolean;
  data?: any;
  hideInfo?: boolean;
  variant?: 'default' | 'iconOnly';
  shape?: 'bullet' | 'bulletInverted';
  icon?: React.ReactNode;
}

export const NodeBox = (props: PropsWithChildren<NodeBoxProps>) => {
  const {
    data = {},
    children,
    onClick,
    className,
    size,
    hideInfo,
    empty = false,
    variant = 'default',
    icon,
    shape = 'bullet',
  } = props;
  const { isZoomedOut, hoverScale } = useZoomState();
  const isIconOnly = variant === 'iconOnly';

  let FooterView;
  const filteredChildren = React.Children.toArray(children).filter((child, i) => {
    if (
      React.isValidElement(child) &&
      (child.type as JSXElementConstructor<any>).name === 'Footer'
    ) {
      FooterView = child;
      return false;
    }
    return true;
  });

  if (isIconOnly) {
    return (
      <div
        onClick={onClick}
        data-testid={props['data-testid']}
        data-id={props['data-id']}
        style={
          isZoomedOut
            ? {
                ['--hover-scale' as string]: hoverScale,
              }
            : undefined
        }
        className={classNames(classes.box, classes.box_iconOnly, className, {
          [classes.box_clickable]: !!onClick,
          [classes.box_zoomedOut]: isZoomedOut,
          [classes.box_shape_bullet]: shape === 'bullet',
          [classes.box_shape_bulletInverted]: shape === 'bulletInverted',
        })}
      >
        <div className={classes.iconContainer}>{icon}</div>
        {filteredChildren.length > 0 && (
          <div className={classes.boxContent}>{filteredChildren}</div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      data-testid={props['data-testid']}
      data-id={props['data-id']}
      style={
        isZoomedOut
          ? {
              ['--hover-scale' as string]: hoverScale,
            }
          : undefined
      }
      className={classNames(classes.box, className, {
        [classes.box_clickable]: !!onClick,
        [classes.box_empty]: empty,
        [classes.box_small]: size === 'small',
        [classes.box_zoomedOut]: isZoomedOut,
      })}
    >
      {hideInfo ? null : (
        <Stack padding="8px" paddingBottom="0px" direction="row" spacing={1}>
          {/* <ChipWithCopy toCopy={data?.id}>ID {data?.id}</ChipWithCopy> */}
          {/*<PipelineStatusBadge*/}
          {/*  size="small"*/}
          {/*  hideInfo*/}
          {/*  id={data?.id}*/}
          {/*  status={data?.status as Status}*/}
          {/*  ingestionMode={data?.ingestion_mode}*/}
          {/*  runtimeStatus={data?.runtime_status}*/}
          {/*/>*/}
        </Stack>
      )}
      <div className={classes.boxContent}>{filteredChildren}</div>
      {FooterView}
    </div>
  );
};

type NodeBoxTitleProps = {
  className?: string;
  infoText?: string;
};

NodeBox.Footer = Footer;

NodeBox.Title = (props: PropsWithChildren<NodeBoxTitleProps>) => {
  const { children, className, infoText } = props;
  return (
    <div className={classNames(classes.name, className)}>
      <Typography className={classes.resourceName} variant="subtitle2">
        {children}
      </Typography>
      {!!infoText && (
        <Tooltip placement="top-start" title={infoText}>
          <InfoIcon />
        </Tooltip>
      )}
    </div>
  );
};
